// Basket + duration state for the Request Access modal.
//
// Holds the selected request rows, the roles ticked on them, the bulk
// duration/reason, and the per-(recipient × item) overrides used in
// "customise individual resources" mode. Derives the per-row "won't be sent"
// warnings and the submittable subset from those. Duration resolution + the
// override key both come from the canonical helpers in payloads.ts.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DurationValue } from "components/ds/DurationField";
import type { BasketItemCfg } from "./BasketTable";
import type { ResolvedRecipient } from "./BasketDrawer";
import {
  dedupeByResource,
  EVERY_RECIPIENT,
  heldIdsForResource,
  resourceIdIndex,
  resourceKey,
  toggleResourceSelection,
  type FlatRequestItem,
} from "./types";
import {
  perItemKey,
  perAccountKey,
  perRoleKey,
  recipientRowStatus,
  resolveItemCfg,
  type RecipientRowStatus,
} from "./payloads";
import {
  basketIndefiniteOnly,
  sameDuration,
  smallestMaxDuration,
} from "./durationLimits";
import { isReasonInvalid } from "./commentRequirement";
import {
  dropResourceRoles,
  itemTerms,
  poolRoles,
  recipientRoles,
  toggleRole,
  type RecipientRoleSelection,
  type RequestTerms,
  type RoleSelection,
} from "./roleSelection";
import {
  recipientItemView,
  recipientOptionsView,
  roleSharing,
  type ItemView,
} from "./recipientViews";
import { MF_ACCESS } from "state/members/access_v2/catalogs";

const DEFAULT_DURATION: DurationValue = { num: 3, unit: "months" };

export interface Basket {
  /** The rows to request: one per selectable resource, duplicates collapsed. */
  basketItems: FlatRequestItem[];
  toggle: (id: string) => void;
  /**
   * Is this browse row in the basket? True for the row itself AND for any
   * other row granting the same selectable resource, so every card for a
   * resource already in the basket reads "Added" (and toggling any of them
   * removes it) instead of quietly adding it a second time.
   */
  isAdded: (id: string) => boolean;
  /** Roles ticked per recipient and resource, for resources whose access
   *  follows them. */
  roleSelection: RecipientRoleSelection;
  rolesFor: (recipientId: string) => RoleSelection;
  /** `everyone` toggles it on the picker every recipient shares, starting
   *  from what is ticked for `recipientId`. */
  toggleRole: (
    recipientId: string,
    item: FlatRequestItem,
    roleId: string,
    everyone?: boolean,
  ) => void;
  /** Each member's own view of a row (their roles and terms). */
  itemView: ItemView;
  /** The row as a recipient sees it, or null when nobody it covers can
   *  request it. */
  recipientItem: (
    recipientId: string,
    item: FlatRequestItem,
  ) => FlatRequestItem | null;
  /** Every recipient who can request the row is offered the same roles and
   *  has the same ones ticked. */
  sharesRoles: (item: FlatRequestItem) => boolean;
  /** The terms of every (member × row) the request covers. */
  basketTerms: RequestTerms[];
  /** Still asking, or failed to ask, what the recipients may request. */
  recipientOptions: {
    loading: boolean;
    error: string | null;
    retry: (() => void) | null;
  };
  /** Empty the basket (used by the mobile "Clear basket" action). */
  clear: () => void;
  bulkDuration: DurationValue;
  setBulkDuration: (v: DurationValue) => void;
  bulkReason: string;
  setBulkReason: (v: string) => void;
  perItem: boolean;
  setPerItemMode: (on: boolean) => void;
  perItemCfg: Record<string, BasketItemCfg>;
  patchPerItem: (
    recipientId: string,
    itemId: string,
    patch: Partial<BasketItemCfg>,
  ) => void;
  /** `${recipientId}__${itemId}__${identityId}` → per-account duration. Each
   *  chosen account is its own request and can carry its own duration. */
  perAccountDuration: Record<string, DurationValue>;
  patchAccountDuration: (
    recipientId: string,
    itemId: string,
    identityId: string,
    duration: DurationValue,
  ) => void;
  /** `${recipientId}__${resourceId}__${roleId}` → per-role duration, on a
   *  resource whose access follows its roles. EVERY_RECIPIENT keys a role's
   *  duration for whoever the request is for. */
  perRoleDuration: Record<string, DurationValue>;
  patchRoleDuration: (
    recipientId: string,
    item: FlatRequestItem,
    roleId: string,
    duration: DurationValue,
  ) => void;
  /** perItemKey(recipientId, itemId) → whether and for whom the row won't be
   *  sent (not requestable for them, no role chosen, a role that can't take a
   *  duration, or a duration over policy max). */
  rowStatus: Record<string, RecipientRowStatus>;
  /** Basket rows that currently pass validation — the submit payload. */
  submittableItems: FlatRequestItem[];
  /** ≥1 submittable resource is flagged requiresComment — drives the
   *  reason field's "required" treatment. */
  anyReasonRequired: boolean;
  /** How many submittable resources require a reason. */
  reasonRequiredCount: number;
  /** Bulk mode: attach the bulk reason only to resources that require it
   *  (default) vs every resource in the request. */
  reasonOnlyRequired: boolean;
  setReasonOnlyRequired: (next: boolean) => void;
  /** A required reason is still missing somewhere — blocks submit so the
   *  user is nudged to add it rather than silently dropping the row. */
  hasMissingRequiredReason: boolean;
}

export const useBasket = (
  flat: FlatRequestItem[],
  recipients: ResolvedRecipient[],
  selfMemberId: string | undefined,
  initialBasket: string[],
  /**
   * Focused catalog item to pre-add when it has exactly ONE selectable
   * resource — so opening the modal on a single-resource item lands with a
   * ready-to-send request instead of requiring an "Add" click.
   */
  autoAddCatalogItemId?: string | null,
): Basket => {
  const [basket, setBasket] = useState<string[]>(initialBasket);
  const [roleSelection, setRoleSelection] = useState<RecipientRoleSelection>(
    {},
  );
  const [bulkDuration, setBulkDurationState] =
    useState<DurationValue>(DEFAULT_DURATION);
  const [bulkReason, setBulkReason] = useState("");
  const [reasonOnlyRequired, setReasonOnlyRequired] = useState(true);
  const [perItem, setPerItem] = useState(false);
  const [perItemCfg, setPerItemCfg] = useState<Record<string, BasketItemCfg>>(
    {},
  );
  // Per-(recipient × item × account) duration overrides — a resource requested
  // for several accounts fans out to one request per account, each able to
  // carry its own duration. Absent an override an account inherits the
  // resource's duration (see resolveAccountDuration).
  const [perAccountDuration, setPerAccountDuration] = useState<
    Record<string, DurationValue>
  >({});
  const [perRoleDuration, setPerRoleDuration] = useState<
    Record<string, DurationValue>
  >({});

  // Once the user picks a duration we stop auto-adjusting it. The public
  // setter (wired to the chips + custom field) marks it dirty; the
  // auto-default effect below uses setBulkDurationState directly so it never
  // trips this flag.
  const durationDirty = useRef(false);
  const setBulkDuration = (v: DurationValue) => {
    durationDirty.current = true;
    setBulkDurationState(v);
  };

  // id → the selectable resource it grants. The same resource is published in
  // several catalogs / under several catalog items, which the browse grid shows
  // as separate cards; the basket holds at most one row per resource.
  const resourceById = useMemo(() => resourceIdIndex(flat), [flat]);

  // Pre-add a focused single-resource item (see param). Guarded by a ref so it
  // seeds at most once per focused item — it must not re-add after the user
  // removes it, and must wait for `flat` to load before deciding the count.
  const autoAddedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoAddCatalogItemId) {
      autoAddedRef.current = null;
      return;
    }
    if (autoAddedRef.current === autoAddCatalogItemId) return;
    const resources = flat.filter(
      (f) => f.catalogItemId === autoAddCatalogItemId,
    );
    if (resources.length === 1) {
      autoAddedRef.current = autoAddCatalogItemId;
      const id = resources[0].id;
      // Resource-keyed, not id-keyed: if a sibling row for the same resource
      // is already in the basket the seed would be appended and then dropped
      // as a duplicate, and the ref above means it never retries.
      setBasket((prev) =>
        heldIdsForResource(id, prev, resourceById).length > 0
          ? prev
          : [...prev, id],
      );
    }
  }, [autoAddCatalogItemId, flat, resourceById]);

  const basketItems = useMemo(
    () =>
      dedupeByResource(
        basket
          .map((id) => flat.find((f) => f.id === id))
          .filter(Boolean) as FlatRequestItem[],
      ),
    [basket, flat],
  );

  const optionMemberIds = useMemo(
    () => recipients.flatMap((r) => r.memberIds),
    [recipients],
  );
  const optionItems = useMemo(
    () =>
      basketItems.map((item) => ({
        catalogItemId: item.catalogItemId,
        selectableResourceId: resourceKey(item),
      })),
    [basketItems],
  );
  const options = MF_ACCESS.useRecipientRequestOptions(
    optionMemberIds,
    optionItems,
    selfMemberId,
  );
  const itemView = useMemo(
    () => recipientOptionsView(options.options, selfMemberId),
    [options.options, selfMemberId],
  );

  const recipientItems = useMemo(() => {
    const out: Record<string, FlatRequestItem | null> = {};
    for (const r of recipients)
      for (const item of basketItems)
        out[perItemKey(r.id, item.id)] = recipientItemView(r, item, itemView);
    return out;
  }, [recipients, basketItems, itemView]);
  const recipientItem = useCallback(
    (recipientId: string, item: FlatRequestItem) => {
      const view = recipientItems[perItemKey(recipientId, item.id)];
      return view === undefined ? item : view;
    },
    [recipientItems],
  );

  useEffect(() => {
    setRoleSelection((s) =>
      basketItems.reduce((acc, item) => {
        const sharing = roleSharing(recipients, item, recipientItem, acc);
        return sharing.shared
          ? poolRoles(acc, item, sharing.recipientIds)
          : acc;
      }, s),
    );
  }, [basketItems, recipients, recipientItem, roleSelection]);

  const basketTerms = useMemo<RequestTerms[]>(() => {
    if (recipients.length === 0)
      return basketItems.map((item) =>
        itemTerms(item, recipientRoles(roleSelection, EVERY_RECIPIENT)),
      );
    return recipients.flatMap((r) => {
      const roles = recipientRoles(roleSelection, r.id);
      return r.memberIds.flatMap((memberId) =>
        basketItems.flatMap((item) => {
          const view = itemView(memberId, item);
          return view ? [itemTerms(view, roles)] : [];
        }),
      );
    });
  }, [recipients, basketItems, itemView, roleSelection]);

  // Smart default: until the user touches it, keep the bulk duration at the
  // most-restrictive cap across the basket (e.g. one resource maxes at 10
  // days, another at 5 hours → default to 5 hours), so the default already
  // fits every selected item. Resources with no finite cap don't constrain;
  // if none do, fall back to DEFAULT_DURATION. A basket holding an
  // indefinite-only resource defaults to Indefinite when everything allows it.
  useEffect(() => {
    if (durationDirty.current) return;
    const fitted = basketIndefiniteOnly(basketTerms)
      ? { ...DEFAULT_DURATION, indefinite: true }
      : (smallestMaxDuration(basketTerms) ?? DEFAULT_DURATION);
    setBulkDurationState((current) =>
      sameDuration(current, fitted) ? current : fitted,
    );
  }, [basketTerms]);

  const cfgContext = {
    perItem,
    perItemCfg,
    bulkDuration,
    bulkReason,
    perAccountDuration,
    perRoleDuration,
  };

  const rowStatus = useMemo<Record<string, RecipientRowStatus>>(() => {
    const out: Record<string, RecipientRowStatus> = {};
    for (const r of recipients)
      for (const item of basketItems)
        out[perItemKey(r.id, item.id)] = recipientRowStatus(
          r,
          item,
          itemView,
          roleSelection,
          cfgContext,
          selfMemberId,
        );
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    basketItems,
    bulkDuration,
    itemView,
    perItem,
    perItemCfg,
    perRoleDuration,
    recipients,
    roleSelection,
    selfMemberId,
  ]);

  const sentRows = (item: FlatRequestItem) =>
    recipients
      .map((r) => ({ r, status: rowStatus[perItemKey(r.id, item.id)] }))
      .filter(({ status }) => status && !status.warning);

  const submittableItems = useMemo(
    () => basketItems.filter((it) => sentRows(it).length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [basketItems, rowStatus, recipients],
  );

  const reasonRequiredCount = useMemo(
    () =>
      submittableItems.filter((it) =>
        sentRows(it).some(({ status }) => status.requiresComment),
      ).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submittableItems, rowStatus, recipients],
  );
  const anyReasonRequired = reasonRequiredCount > 0;

  // A required reason is missing somewhere. In bulk mode that's just the one
  // shared reason; in per-item mode every (recipient × required item) cell
  // needs its own. Mirrors the server's extend/request comment gate — a UX
  // guardrail, not the enforcement boundary.
  const hasMissingRequiredReason = useMemo(() => {
    const perItemRows = perItem
      ? submittableItems.flatMap((it) =>
          sentRows(it).map(({ r, status }) => ({
            requiresComment: status.requiresComment,
            reason: resolveItemCfg(r.id, it.id, cfgContext).reason,
          })),
        )
      : [];
    return isReasonInvalid({
      perItem,
      anyRequiresComment: anyReasonRequired,
      bulkReason,
      perItemRows,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    submittableItems,
    rowStatus,
    recipients,
    perItem,
    perItemCfg,
    bulkReason,
    anyReasonRequired,
  ]);

  const patchPerItem = (
    recipientId: string,
    itemId: string,
    patch: Partial<BasketItemCfg>,
  ) => {
    const k = perItemKey(recipientId, itemId);
    setPerItemCfg((c) => {
      const existing = c[k] ?? { duration: bulkDuration, reason: bulkReason };
      return { ...c, [k]: { ...existing, ...patch } };
    });
  };

  const patchAccountDuration = (
    recipientId: string,
    itemId: string,
    identityId: string,
    duration: DurationValue,
  ) => {
    const k = perAccountKey(recipientId, itemId, identityId);
    setPerAccountDuration((c) => ({ ...c, [k]: duration }));
  };

  const patchRoleDuration = (
    recipientId: string,
    item: FlatRequestItem,
    roleId: string,
    duration: DurationValue,
  ) => {
    const k = perRoleKey(recipientId, resourceKey(item), roleId);
    setPerRoleDuration((c) => ({ ...c, [k]: duration }));
  };

  const dropRoles = (resourceId: string) =>
    setRoleSelection((s) => dropResourceRoles(s, resourceId));

  // Turning "customise" on seeds every (recipient × basket item) cell with
  // the current bulk values so each row starts from the bulk default.
  const setPerItemMode = (on: boolean) => {
    if (on) {
      setPerItemCfg((c) => {
        const next = { ...c };
        recipients.forEach((r) => {
          basket.forEach((iid) => {
            const k = perItemKey(r.id, iid);
            if (!next[k])
              next[k] = { duration: bulkDuration, reason: bulkReason };
          });
        });
        return next;
      });
    }
    setPerItem(on);
  };

  return {
    basketItems,
    toggle: (id) => {
      const resourceId = resourceById.get(id);
      if (
        resourceId &&
        heldIdsForResource(id, basket, resourceById).includes(id)
      )
        dropRoles(resourceId);
      setBasket((b) => toggleResourceSelection(b, id, resourceById));
    },
    isAdded: (id) => heldIdsForResource(id, basket, resourceById).length > 0,
    roleSelection,
    rolesFor: (recipientId) => recipientRoles(roleSelection, recipientId),
    toggleRole: (recipientId, item, roleId, everyone) =>
      setRoleSelection((s) =>
        toggleRole(
          s,
          recipientId,
          item,
          roleId,
          everyone
            ? roleSharing(recipients, item, recipientItem, s).recipientIds
            : undefined,
        ),
      ),
    itemView,
    recipientItem,
    sharesRoles: (item) =>
      roleSharing(recipients, item, recipientItem, roleSelection).shared,
    basketTerms,
    recipientOptions: {
      loading: options.loading,
      error: options.error,
      retry: options.retry,
    },
    clear: () => {
      setBasket([]);
      setRoleSelection({});
      setPerRoleDuration({});
    },
    bulkDuration,
    setBulkDuration,
    bulkReason,
    setBulkReason,
    perItem,
    setPerItemMode,
    perItemCfg,
    patchPerItem,
    perAccountDuration,
    patchAccountDuration,
    perRoleDuration,
    patchRoleDuration,
    rowStatus,
    submittableItems,
    anyReasonRequired,
    reasonRequiredCount,
    reasonOnlyRequired,
    setReasonOnlyRequired,
    hasMissingRequiredReason,
  };
};
